import React from 'react';
import { Box, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { usePlan } from '../../context/PlanContext';

export type SeatTone = 'normal' | 'warning' | 'error';

/** Warning from 80% of the cap, error at (or past) it. */
export function seatTone(used: number, cap: number): SeatTone {
    if (used >= cap) return 'error';
    if (used >= cap * 0.8) return 'warning';
    return 'normal';
}

const PROGRESS_COLOR: Record<SeatTone, 'primary' | 'warning' | 'error'> = {
    normal: 'primary',
    warning: 'warning',
    error: 'error',
};

/** "8 / 10 employees" for a Free plan institution. Renders nothing on a paid (uncapped) plan. */
export default function SeatMeter() {
    const { t } = useTranslation('users');
    const { plan } = usePlan();
    if (!plan || plan.planTier !== 'FREE' || plan.employeeCap == null || plan.employeeCap <= 0) return null;

    const used = plan.employeeCount;
    const cap = plan.employeeCap;
    const tone = seatTone(used, cap);
    const percent = Math.min(100, Math.round((used / cap) * 100));
    const meterText = t('userList.seats.meter', { used, cap });

    return (
        <Paper variant="outlined" data-testid="seat-meter" data-tone={tone} sx={{ px: 2, py: 1.5, mb: 2.5, maxWidth: 420 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.75 }}>
                <Typography variant="caption" color="text.secondary">{t('userList.seats.label')}</Typography>
                <Typography variant="body2" fontWeight={600} color={tone === 'normal' ? 'text.primary' : `${tone}.main`}>
                    {meterText}
                </Typography>
            </Stack>
            <LinearProgress
                variant="determinate"
                value={percent}
                color={PROGRESS_COLOR[tone]}
                aria-label={meterText}
                sx={{ height: 6, borderRadius: 3 }}
            />
            {tone !== 'normal' && (
                <Box sx={{ mt: 0.75 }}>
                    <Typography variant="caption" color={`${tone}.main`}>
                        {tone === 'error' ? t('userList.seats.atCap') : t('userList.seats.nearCap', { cap })}
                    </Typography>
                </Box>
            )}
        </Paper>
    );
}
